
import React, { useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { openUrl } from '@/utils/browserHelper';
import { Mail, PawPrint } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Analytics } from '@/services/analytics';

const TOS_URL = 'https://octoveto.com/tos';
const PRIVACY_URL = 'https://octoveto.com/privacy';

interface SignUpFormProps {
  loading: boolean;
  setLoading: (loading: boolean) => void;
}

const inputClass = "w-full h-12 pl-10 pr-4 rounded-xl text-sm text-foreground placeholder:text-muted-foreground/60 outline-none transition-all";
const inputStyle = {
  background: 'rgba(255,255,255,0.05)',
  border: '1.5px solid rgba(255,193,7,0.45)',
} as React.CSSProperties;

const SignUpForm: React.FC<SignUpFormProps> = ({ loading, setLoading }) => {
  const { signInWithMagicLink } = useAuth();
  const { t } = useTranslation('auth');
  const [email, setEmail] = useState('');
  const [tosAccepted, setTosAccepted] = useState(false);
  const [sent, setSent] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!tosAccepted) {
      toast.error(t('signup_tos_required'));
      return;
    }
    setLoading(true);
    try {
      const { error } = await signInWithMagicLink(email);
      if (error) {
        toast.error(`Erreur : ${error.message}`);
      } else {
        Analytics.signupCompleted('magic_link');
        setSent(true);
      }
    } catch {
      toast.error(t('signup_error_send'));
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    setResendLoading(true);
    try {
      const { error } = await signInWithMagicLink(email);
      if (error) {
        toast.error(t('signup_error_resend'));
      } else {
        toast.success(t('magic_link_resent'));
      }
    } finally {
      setResendLoading(false);
    }
  };

  if (sent) {
    return (
      <div className="space-y-4 text-center py-4">
        <div className="text-5xl">📧</div>
        <h3 className="font-bold text-lg text-foreground">{t('magic_check_inbox')}</h3>
        <p className="text-sm text-muted-foreground leading-relaxed">
          {t('magic_sent_text', { email }).replace('<1>', '').replace('</1>', '')}<br />
          {t('signup_magic_sent_text', { email }).split('\n')[1]}
        </p>
        <button
          type="button"
          onClick={handleResend}
          disabled={resendLoading}
          className="w-full h-11 rounded-full text-sm font-medium text-foreground/80 border border-white/15 hover:bg-white/5 transition-all disabled:opacity-50"
        >
          {resendLoading ? t('magic_resending') : t('magic_resend')}
        </button>
        <button
          type="button"
          className="text-xs text-muted-foreground underline w-full text-center"
          onClick={() => { setSent(false); setEmail(''); }}
        >
          {t('signup_use_another_email')}
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4 pt-1">
      <div className="space-y-1.5">
        <label className="text-sm font-medium text-foreground/90">{t('email_label')}</label>
        <div className="relative">
          <Mail size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-primary/70 pointer-events-none" />
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder={t('email_placeholder')}
            required
            className={inputClass}
            style={inputStyle}
            onFocus={e => { e.currentTarget.style.border = '1.5px solid #FFC107'; e.currentTarget.style.boxShadow = '0 0 0 3px rgba(255,193,7,0.15)'; }}
            onBlur={e => { e.currentTarget.style.border = '1.5px solid rgba(255,193,7,0.45)'; e.currentTarget.style.boxShadow = 'none'; }}
          />
        </div>
      </div>

      <label className="flex items-start gap-3 cursor-pointer group">
        <div className="relative mt-0.5 flex-shrink-0">
          <input
            type="checkbox"
            checked={tosAccepted}
            onChange={(e) => setTosAccepted(e.target.checked)}
            className="sr-only"
          />
          <div
            className="w-5 h-5 rounded-md border transition-all"
            style={{
              background: tosAccepted ? '#FFC107' : 'rgba(255,255,255,0.05)',
              border: tosAccepted ? '1.5px solid #FFC107' : '1.5px solid rgba(255,193,7,0.45)',
              boxShadow: tosAccepted ? '0 0 0 3px rgba(255,193,7,0.15)' : 'none',
            }}
          >
            {tosAccepted && (
              <svg viewBox="0 0 12 12" fill="none" className="w-full h-full p-0.5">
                <path d="M2 6l3 3 5-5" stroke="#000" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            )}
          </div>
        </div>
        <span className="text-xs text-muted-foreground leading-relaxed">
          {t('signup_accept_tos_before')}{' '}
          <button
            type="button"
            onClick={() => openUrl(TOS_URL)}
            className="text-primary underline hover:text-primary/80 font-medium"
          >
            {t('signup_tos')}
          </button>
          {' '}{t('signup_accept_tos_between')}{' '}
          <button
            type="button"
            onClick={() => openUrl(PRIVACY_URL)}
            className="text-primary underline hover:text-primary/80 font-medium"
          >
            <PawPrint size={10} className="inline mr-0.5" />
            {t('signup_privacy')}
          </button>
        </span>
      </label>

      <button
        type="submit"
        disabled={loading || !email}
        className="w-full rounded-full font-bold text-base text-black transition-all active:scale-95 disabled:opacity-60 mt-1"
        style={{
          height: '52px',
          background: 'linear-gradient(135deg, #FFC107 0%, #E6A800 100%)',
          boxShadow: '0 4px 20px rgba(255,193,7,0.4)',
        }}
      >
        {loading ? t('magic_sending') : t('magic_link_button')}
      </button>
    </form>
  );
};

export default SignUpForm;
